import {
  DocumentPdfPane,
  DocumentViewer,
  type DocumentPaneRenderProps,
  type DocumentViewerHighlight,
  type DocumentViewerLayout,
} from '@aecom-ai-engineering/react-components';
import { useGetVersionComparisonQuery } from '@consigli/hooks';
import { type VersionComparison, type VersionCompareReviewStatus } from '@consigli/types';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReconciliationBadge } from './components/reconciliation-badge';
import { ReviewCell } from './components/review-cell';

interface VersionComparePdfPanelProps {
  comparison: VersionComparison;
  projectId: number;
  packageId: string;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
  onReviewChange: (id: number, status: VersionCompareReviewStatus) => void;
}

// Findings store 0-based page indices, but the DocumentViewer is 1-based.
const buildHighlights = (
  x0: number | null | undefined,
  y0: number | null | undefined,
  x1: number | null | undefined,
  y1: number | null | undefined,
  pageNumber: number | null | undefined,
): DocumentViewerHighlight[] | undefined => {
  if (x0 == null || y0 == null || x1 == null || y1 == null || pageNumber == null) return undefined;
  return [{ x0, y0, x1, y1, pageNumber: pageNumber + 1 }];
};

// The AECOM DocumentViewer re-parses the PDF on every mount and keeps no
// cross-mount cache, and the detail endpoint re-signs the blob (fresh SAS query
// string) on every call — so closing and reopening a same-document finding
// otherwise re-downloads it, and even the browser's HTTP cache misses. Pin the
// first signed URL minted for a blob (bounded by the SAS lifetime) so every later
// resolve reuses one URL string: `src` stays stable across navigation *and* across
// close/reopen, and the bytes come from the browser cache instead of the network.
const SIGNED_URL_TTL_MS = 30 * 60 * 1000;
const blobUrlCache = new Map<string, { url: string; expiresAt: number }>();

const stableBlobUrl = (
  blobId: string | null | undefined,
  freshUrl: string | null | undefined,
): string | null | undefined => {
  if (blobId == null) return freshUrl;
  const cached = blobUrlCache.get(blobId);
  if (cached && cached.expiresAt > Date.now()) return cached.url;
  if (freshUrl) {
    blobUrlCache.set(blobId, { url: freshUrl, expiresAt: Date.now() + SIGNED_URL_TTL_MS });
  }
  return freshUrl;
};

// The signed read URLs are SAS-generated on demand by the detail endpoint, so
// they lag the selection by a request. Hold the last comparison that resolved a
// URL for the PDF panes, so stepping through findings swaps the document in place
// instead of tearing the drawer down while the next URL loads.
type ResolvedComparison = {
  comparison: VersionComparison;
  fileUrl: string | null | undefined;
  refFileUrl: string | null | undefined;
};

// `doc` is the finding whose signed URLs are painted in the panes (it may lag the
// selection by a request); `comparison` is the live selection that drives the
// sidebar, so its review control and text stay in lockstep with the table's row
// highlight while the next document loads.
interface ResolvedPanelProps {
  comparison: VersionComparison;
  resolved: ResolvedComparison;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
  onReviewChange: (id: number, status: VersionCompareReviewStatus) => void;
}

const ResolvedPanel: FC<ResolvedPanelProps> = ({
  comparison,
  resolved,
  onClose,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  onReviewChange,
}) => {
  const { t } = useTranslation();

  // The drawer opens stacked so both documents fit the narrow pane; maximizing
  // switches to side-by-side, where there's room. The manual toggle still wins
  // within the current size.
  const maximizedRef = useRef(false);
  const [layout, setLayout] = useState<DocumentViewerLayout>('stacked');
  const handleExpand = useCallback(() => {
    maximizedRef.current = !maximizedRef.current;
    setLayout(maximizedRef.current ? 'side-by-side' : 'stacked');
  }, []);

  const doc = resolved.comparison;
  const hasA = Boolean(resolved.fileUrl);
  const isComparison = Boolean(resolved.fileUrl && resolved.refFileUrl);
  const missingName = t('error.missing-pdf-name');

  // A finding may carry only version A, only version B, or both. Whichever side
  // exists becomes the primary; the reference is the secondary only when both do.
  const primarySrc = (hasA ? resolved.fileUrl : resolved.refFileUrl) as string;
  const primaryName = (hasA ? doc.blobName : doc.refBlobName) ?? missingName;
  const primaryInitialPage = ((hasA ? doc.pageNumber : doc.refPageNumber) ?? 0) + 1;
  const secondaryInitialPage = (doc.refPageNumber ?? 0) + 1;

  // The header badge always renders a check icon, so surface this record's page-match
  // confidence there; fall back to the document name for findings that have no A->B
  // page pairing (e.g. added/removed content), which would otherwise show "Found".
  const headerLabel =
    doc.matchConfidence != null
      ? t('version-compare.reconciliation.group-match-confidence', { value: doc.matchConfidence })
      : primaryName;

  const primaryHighlights = useMemo(
    () =>
      hasA
        ? buildHighlights(doc.x0, doc.y0, doc.x1, doc.y1, doc.pageNumber)
        : buildHighlights(doc.refX0, doc.refY0, doc.refX1, doc.refY1, doc.refPageNumber),
    [doc, hasA],
  );
  const secondaryHighlights = useMemo(
    () =>
      isComparison
        ? buildHighlights(doc.refX0, doc.refY0, doc.refX1, doc.refY1, doc.refPageNumber)
        : undefined,
    [doc, isComparison],
  );

  // 2.0.0 moved per-document options (page, highlights, scroll) into a pane
  // renderer keyed by file type; the primary and secondary panes share it, so
  // switch on `src` to hand each side its own page and highlights.
  const renderers = useMemo(
    () => ({
      pdf: (props: DocumentPaneRenderProps) => (
        <DocumentPdfPane
          active={props.active}
          caption={props.caption}
          onSidebarContextChange={props.onSidebarContextChange}
          src={props.src}
          initialPage={props.src === primarySrc ? primaryInitialPage : secondaryInitialPage}
          highlights={props.src === primarySrc ? primaryHighlights : secondaryHighlights}
          continuousScroll
        />
      ),
    }),
    [primarySrc, primaryInitialPage, secondaryInitialPage, primaryHighlights, secondaryHighlights],
  );

  const sidebar: ReactNode = (
    <div className="flex flex-col gap-4 text-day-neutral-dark">
      <div className="text-sm font-semibold">{comparison.changeDescription}</div>
      {comparison.content && (
        <div className="text-sm">
          <div className="font-bold">{t('version-compare.reconciliation.col-version-a')}:</div>
          <div className="max-h-40 overflow-y-auto italic text-day-neutral-subtle">
            {comparison.content}
          </div>
        </div>
      )}
      {comparison.refContent && (
        <div className="text-sm">
          <div className="font-bold">{t('version-compare.reconciliation.col-version-b')}:</div>
          <div className="max-h-40 overflow-y-auto italic text-day-neutral-subtle">
            {comparison.refContent}
          </div>
        </div>
      )}
      {comparison.commentText && (
        <div className="text-sm">
          <div className="font-bold">{t('version-compare.reconciliation.col-comment-text')}:</div>
          <div className="max-h-40 overflow-y-auto italic text-day-neutral-subtle">
            {comparison.commentText}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          {t('version-compare.reconciliation.col-reconciliation')}
        </span>
        <ReconciliationBadge
          status={comparison.reconciliationStatus}
          confidence={comparison.reconciliationConfidence}
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          {t('version-compare.reconciliation.col-review')}
        </span>
        <div className="w-fit">
          <ReviewCell
            id={comparison.id}
            value={comparison.reviewStatus}
            onChange={(status) => onReviewChange(comparison.id, status)}
            align="left"
          />
        </div>
      </div>
    </div>
  );

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      src={primarySrc}
      secondarySrc={isComparison ? (resolved.refFileUrl as string) : undefined}
      title={primaryName}
      headerLabel={headerLabel}
      primaryTitle={isComparison ? primaryName : undefined}
      secondaryTitle={isComparison ? (doc.refBlobName ?? missingName) : undefined}
      layout={isComparison ? layout : undefined}
      onLayoutChange={isComparison ? setLayout : undefined}
      onExpand={isComparison ? handleExpand : undefined}
      showSidebar
      showNavigationActions
      previousDisabled={!hasPrevious}
      nextDisabled={!hasNext}
      onPrevious={onPrevious}
      onNext={onNext}
      renderers={renderers}
    >
      {sidebar}
    </DocumentViewer>
  );
};

export const VersionComparePdfPanel: FC<VersionComparePdfPanelProps> = ({
  comparison,
  projectId,
  packageId,
  onClose,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  onReviewChange,
}) => {
  const { data: detail } = useGetVersionComparisonQuery({
    projectId,
    packageId,
    id: comparison.id,
  });
  const fileUrl = detail?.fileUrl;
  const refFileUrl = detail?.refFileUrl;

  const [resolved, setResolved] = useState<ResolvedComparison | null>(null);
  useEffect(() => {
    // Resolve through the per-blob cache so a finding in the same document as the
    // last one (whether stepped to or reopened) reuses the exact `src` string: the
    // pane then just jumps page + re-highlights instead of remounting/redownloading.
    const nextFileUrl = stableBlobUrl(comparison.blobId, fileUrl);
    const nextRefFileUrl = stableBlobUrl(comparison.refBlobId, refFileUrl);
    // Nothing to paint yet (detail still loading for a blob we've not cached): keep
    // the previous document on screen rather than tearing the drawer down.
    if (!nextFileUrl && !nextRefFileUrl) return;
    setResolved({ comparison, fileUrl: nextFileUrl, refFileUrl: nextRefFileUrl });
  }, [comparison, fileUrl, refFileUrl]);

  if (!resolved) return null;

  return (
    <ResolvedPanel
      comparison={comparison}
      resolved={resolved}
      onClose={onClose}
      onNext={onNext}
      onPrevious={onPrevious}
      hasNext={hasNext}
      hasPrevious={hasPrevious}
      onReviewChange={onReviewChange}
    />
  );
};
